<script setup lang="ts">
import { WidgetCardInstructionsModal } from '~/components/widget/card-instructions-modal'
import { WidgetBreadcrumbs } from '~/components/widget/breadcrumbs'
import type { IResponseData } from '~/types'
import { WidgetAboutPageHeader, mapItemToSchemaData } from '~/components/widget/about-page'
import { WidgetOrganizationAsideContacts } from '~/components/widget/organization-aside-contacts'
import type { ICard } from '~/components/entity/card'
import {
  WidgetCardPageBeneficiaries,
  WidgetCardPageInfoItems,
  WidgetCardPageOptions,
  WidgetCardPageTermsOfUse,
  WidgetCardPageHeaderSkeleton
} from '~/components/widget/card-page'
import { ContentBlock } from '~/components/ui/content-block'
import { useSeoFromEntity } from '~/composables/useSeoFromEntity'
import { WidgetReviveAd } from '~/components/widget/revive-ad'
import { FeatureCardFormRequest } from '~/components/feature/card/form-request'
import { WidgetRelatedProducts } from '~/components/widget/related-products'
import { Disclaimer } from '~/components/ui/disclaimer'
import { WidgetRating } from '~/components/widget/rating'

const { t, locale } = useI18n()
const route = useRoute()
const cardUrl = `/card/${route.params.card}`
const { data, status } = await useApiWith404<IResponseData<ICard>>(() => cardUrl, {
  key: `banks-card-${route.params.bank}-${route.params.card}`
})
const card = computed(() => data.value?.data)
const config = useRuntimeConfig()
const baseUrl = config.public.siteUrl
useSeoFromEntity(card, baseUrl, locale.value)
const breadcrumbs = computed(() => [
  { title: t('common.banks_of_kyrgyzstan'), url: '/banks' },
  { title: card.value?.organization.title || t('common.bank'), url: `/bank/${route.params.bank}` },
  { title: t('common.bank_cards'), url: `/bank/${route.params.bank}/cards` },
  { title: card.value?.title || t('common.bank_card'), url: card.value?.url }
])

if (
  card.value &&
  card.value.organization?.slug &&
  card.value.organization?.slug !== route.params.bank
) {
  showError({ statusCode: 404 })
}

const hasOptions = ref(false)
const hasBeneficiaries = ref(false)

const showCardInstructionsModal = computed(() => {
  return card.value?.organization?.permissions?.is_card_instructions_enabled
})

const handleOptionsStatusChange = (hasItems: boolean) => {
  hasOptions.value = hasItems
}

const handleBeneficiariesStatusChange = (hasItems: boolean) => {
  hasBeneficiaries.value = hasItems
}

const fitItem = computed(() => mapItemToSchemaData(card.value, t('product.inactive_info')))
</script>

<template>
  <Main>
    <Container>
      <WidgetBreadcrumbs :links="breadcrumbs" />
      <ContentLayout>
        <template #right-sidebar>
          <aside>
            <WidgetOrganizationAsideContacts v-if="card" :organization="card.organization" />
            <WidgetCardInstructionsModal v-if="showCardInstructionsModal" />
            <WidgetReviveAd zoneid="27" class="overflow-hidden rounded-lg" />
          </aside>
        </template>
        <div class="space-y-5" role="main">
          <WidgetAboutPageHeader v-if="card" type="card" :product="fitItem">
            <WidgetCardPageInfoItems
              v-if="card"
              :card="card"
              class="border-b border-gray-200 pb-4"
            />
            <div class="overflow-hidden">
              <figure v-if="card.logo" class="float-left mr-4">
                <NuxtImg
                  :src="card.logo"
                  :alt="card.title"
                  class="pointer-events-none mr-auto hidden h-auto w-[18.5rem] select-none object-contain lg:block"
                  format="avif,webp"
                  fetchpriority="high"
                />
              </figure>
              <Content>
                <div v-hydrated-html="card.description" class="card-description" />
              </Content>
            </div>
          </WidgetAboutPageHeader>
          <WidgetCardPageHeaderSkeleton v-else />

          <WidgetReviveAd zoneid="40" class="overflow-hidden rounded-lg" />

          <WidgetCardPageTermsOfUse v-if="card" :card="card" />

          <section v-show="hasOptions || hasBeneficiaries">
            <ContentBlock v-if="card" variant="white">
              <div class="grid grid-cols-1 md:grid-cols-4">
                <div v-show="hasOptions" class="space-y-5 md:col-span-2">
                  <Heading tag="h2" class="h4">{{
                    $t('widget.card_page.headings.options')
                  }}</Heading>
                  <WidgetCardPageOptions
                    :card="card"
                    @options-status-change="handleOptionsStatusChange"
                  />
                </div>
                <div v-show="hasBeneficiaries" class="space-y-5 md:col-span-2">
                  <Heading tag="h2" class="h4">{{
                    $t('widget.card_page.headings.beneficiaries')
                  }}</Heading>
                  <WidgetCardPageBeneficiaries
                    :card="card"
                    @beneficiaries-status-change="handleBeneficiariesStatusChange"
                  />
                </div>
              </div>
            </ContentBlock>
          </section>
          <WidgetRating
            v-if="card && config.public.widgetRatingEnabled"
            :entity="card"
            entity-type="card"
            :heading-text="$t('page.card.rating_heading', { title: card.title })"
          />
          <Disclaimer
            :title="$t('common.product.info_disclaimer.title')"
            :description="$t('common.product.info_disclaimer.description')"
          />
          <FeatureCardFormRequest
            v-if="card && card.organization?.permissions?.is_card_request_allowed"
            :card-id="card.id"
            :card-extended-form="card.extended_form"
            :organization-id="card.organization?.id"
            :organization-type="card.organization_type"
          />
          <WidgetRelatedProducts
            v-if="card?.related_products && card?.related_products.length"
            :products="card.related_products"
            :status="status"
          />
        </div>
      </ContentLayout>
    </Container>
  </Main>
</template>

<style scoped>
html {
  scroll-behavior: smooth;
}

/* The description is passed as <Content><div class="card-description" v-html/></Content>,
   so this wrapper is the only direct child of `.content` and its `space-y-5` stack —
   which compiles to `> * + *` — has nothing to match. Re-apply the spacing here. */
:deep(.card-description) {
  @apply space-y-5;
}

/* The card logo is a `float-left` figure, so only the line boxes inside a list item
   shorten to clear it — the item's block box and its marker stay at the left edge,
   stranded behind the image. `display: flow-root` gives the list its own block
   formatting context, so it shifts aside as a whole and the markers travel with
   the text. */
:deep(.card-description) :is(ul, ol) {
  display: flow-root;
}
</style>
